<template>
  <div v-if="(exercises && exercises.length > 0) || edit">
    <h2 class="display-6 mb-4 d-flex align-items-center gap-2">
      <font-awesome-icon icon="fa-solid fa-anchor" />
      {{ $t('workout.training') }}
    </h2>

    <div class="card  p-2">
      <!-- Desktop -->
      <div class="table-responsive d-none d-md-block">
        <table class="table table-borderless table-sm align-middle text-center">
          <thead>
            <tr>
              <th>#</th>
              <th colspan="2">
                <font-awesome-icon icon="fa-solid fa-medal" />
                {{ $t('workout.exercise') }}
              </th>
              <th v-for="(set, index) in getMaxSets(exercises)" :key="`set-${index}`">
                <font-awesome-icon icon="fa-solid fa-fire-flame-curved" />
                {{ $t('workout.set') }} {{ index + 1 }}
              </th>
              <th v-if="edit" />
            </tr>
          </thead>
          <tbody
            v-for="(ex, exIndex) in exercises"
            :key="exIndex"
            @dragover.prevent="onDragOver($event, exIndex)"
            @dragleave="onDragLeave($event)"
            @drop="onDrop($event, exIndex)"
            :class="{ 'drag-over': dragOverIndex === exIndex }">
            <tr>
              <td v-if="!edit">
                {{ exIndex + 1 }}
              </td>
              <td v-else draggable="true" @dragstart="onDragStart($event, exIndex)">
                <div class="btn-group w-100">
                  <button type="button" class="btn btn-sm w-50">
                    <font-awesome-icon icon="fa-solid fa-sort" />
                  </button>
                  <button type="button" class="btn btn-sm btn-outline-danger" @click="removeExercise(exIndex)">
                    <font-awesome-icon icon="fa-solid fa-trash" />
                  </button>
                </div>
              </td>
              <td>
                <input
                  v-if="edit"
                  v-model="ex.name"
                  type="text"
                  class="form-control"
                  :placeholder="$t('workout.default_ex')"
                  required
                  maxlength="150"
                  @focus="filterOptions(exIndex)"
                  @input="filterOptions(exIndex)" />
                <span v-else>{{ ex.name }}</span>
                <ul
                  v-if="showDropdownIndex === exIndex && filteredOptions(ex.name).length > 0"
                  class="dropdown-menu show">
                  <li v-for="(option, idx) in filteredOptions(ex.name)" :key="idx">
                    <button
                      class="dropdown-item"
                      type="button"
                      @mousedown.prevent="selectExerciseName(exIndex, option)">
                      {{ option }}
                    </button>
                  </li>
                </ul>
              </td>
              <td>
                <div class="d-flex flex-column align-items-center gap-2">
                  <font-awesome-icon icon="fa-solid fa-weight-hanging" class="mb-2" />
                  <font-awesome-icon icon="fa-solid fa-repeat" />
                </div>
              </td>
              <td v-for="(set, setIndex) in ex.sets" :key="setIndex">
                <div class="d-flex flex-column gap-1">
                  <input
                    v-if="edit"
                    v-model="set.weight"
                    class="form-control"
                    :class="{ 'is-invalid': ex.errors?.weight[setIndex] }"
                    type="number"
                    inputmode="decimal"
                    min="0"
                    max="999"
                    step="0.1"
                    :placeholder="$t('workout.weight')"
                    required
                    @input="updateValue" />
                  <span v-else class="font-monospace">{{ set.weight }}</span>

                  <input
                    v-if="edit"
                    v-model="set.reps"
                    class="form-control"
                    :class="{ 'is-invalid': ex.errors?.reps[setIndex] }"
                    type="number"
                    inputmode="numeric"
                    min="0"
                    max="999"
                    :placeholder="$t('workout.reps')"
                    required
                    @input="updateValue" />
                  <span v-else class="font-monospace">{{ set.reps }}</span>
                </div>
              </td>
              <td v-if="edit" class="d-flex flex-column gap-1">
                <button class="btn btn-outline-dark" type="button" @click="addSet(exIndex)">
                  <font-awesome-icon icon="fa-solid fa-circle-plus" />
                </button>
                <button class="btn btn-outline-dark" type="button" @click="removeSet(exIndex)">
                  <font-awesome-icon icon="fa-solid fa-circle-minus" />
                </button>
              </td>
            </tr>
          </tbody>
          <caption v-if="totalWeight">
            <small>{{ $t('workout.total') }}: {{ totalWeight }}</small>
          </caption>
        </table>
      </div>

      <!-- Mobile -->
      <div class="list-group d-md-none border-0">
        <div
          v-for="(ex, exIndex) in exercises"
          :key="`mobile-${exIndex}`"
          class="mb-2"
          @dragover.prevent="onDragOver($event, exIndex)"
          @dragleave="onDragLeave($event)"
          @drop="onDrop($event, exIndex)"
          :class="{ 'drag-over': dragOverIndex === exIndex }">
          <!-- Exercise name -->
          <div class="list-group-item d-flex align-items-center bg-dark-subtle gap-1">
            <template v-if="edit">
              <input
                v-model="ex.name"
                type="text"
                class="form-control"
                :placeholder="$t('workout.default_ex')"
                maxlength="150"
                required
                @focus="openExerciseSelector(exIndex, ex.name)"
                readonly />
              <button
                type="button"
                class="btn btn-sm btn-outline-danger"
                aria-label="Remove"
                @click="removeExercise(exIndex)">
                <font-awesome-icon icon="fa-solid fa-trash" />
              </button>
            </template>
            <template v-else>
              <span class="text-dark small">{{ ex.name }}</span>
            </template>
          </div>
          <!-- Sets table -->
          <div class="list-group-item p-0">
            <table class="table table-borderless table-sm font-monospace table-hover mb-0">
              <tbody>
                <tr>
                  <td class="align-middle">
                    <font-awesome-icon icon="fa-solid fa-weight-hanging" />
                  </td>
                  <td v-for="(set, setIndex) in ex.sets" :key="`weight-${setIndex}`">
                    <input
                      v-if="edit"
                      v-model="set.weight"
                      type="number"
                      inputmode="decimal"
                      min="0"
                      max="999"
                      class="form-control"
                      :class="{ 'is-invalid': ex.errors?.weight[setIndex] }"
                      :placeholder="$t('workout.weight')"
                      required
                      @input="updateValue" />
                    <span v-else>{{ set.weight }}</span>
                  </td>
                </tr>
                <tr>
                  <td class="align-middle">
                    <font-awesome-icon icon="fa-solid fa-repeat" />
                  </td>
                  <td v-for="(set, setIndex) in ex.sets" :key="`reps-${setIndex}`">
                    <input
                      v-if="edit"
                      v-model="set.reps"
                      class="form-control"
                      :class="{ 'is-invalid': ex.errors?.reps[setIndex] }"
                      type="number"
                      inputmode="numeric"
                      min="0"
                      max="999"
                      :placeholder="$t('workout.reps')"
                      required
                      @input="updateValue" />
                    <span v-else>{{ set.reps }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Buttons for sets -->
          <div v-if="edit" class="list-group-item d-flex gap-1">
            <button class="btn btn-sm w-50" draggable="true" type="button" @dragstart="onDragStart($event, exIndex)">
              <font-awesome-icon icon="fa-solid fa-sort" />
            </button>
            <button type="button" class="btn btn-outline-dark btn-sm w-100" @click="addSet(exIndex)">
              <font-awesome-icon icon="fa-solid fa-circle-plus" /> Set
            </button>
            <button type="button" class="btn btn-outline-dark btn-sm w-100" @click="removeSet(exIndex)">
              <font-awesome-icon icon="fa-solid fa-circle-minus" /> Set
            </button>
          </div>
        </div>

        <!-- Total -->
        <small v-if="totalWeight" class="p-2">
          {{ $t('workout.total') }}: {{ totalWeight }}
        </small>
      </div>

      <!-- OFFCANVAS exercise selector -->
      <div
        class="offcanvas offcanvas-bottom show"
        tabindex="-1"
        v-if="offcanvasVisible"
        style="visibility: visible; height: 70vh;">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title">{{ $t('info.choose_ex') }}</h5>
          <button type="button" class="btn-close" @click="closeExerciseSelector" />
        </div>
        <div class="offcanvas-body">
          <input v-model="searchQuery" type="search" inputmode="search" class="form-control mb-3 bg-dark-subtle" placeholder="Search" />
          <ul class="list-group">
            <li
              v-for="(option, idx) in filteredOptions(searchQuery)"
              :key="idx"
              class="list-group-item"
              @click="selectExerciseName(activeExerciseIndex, option)"
              @keypress="selectExerciseName(activeExerciseIndex, option)">
              {{ option }}
            </li>
          </ul>
          <button type="button" class="btn btn-dark w-100 mt-3" @click="selectExerciseName(activeExerciseIndex, searchQuery)">
            {{ $t('buttons.submit') }}
          </button>
        </div>
      </div>
      <div v-if="edit" class="p-2 text-center">
        <button type="button" class="btn btn-outline-dark" @click="addExercise">
          <font-awesome-icon icon="fa-solid fa-circle-plus" />
          {{ $t('workout.new') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onBeforeMount } from 'vue';
import { useI18n } from 'vue-i18n';
import { getUniqueExs } from '../../../api/api';

const { t } = useI18n();

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  edit: {
    type: Boolean,
    default: false,
  },
  totalWeight: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['update:modelValue', 'update:isValid']);

const exercises = ref(props.modelValue);
const offcanvasVisible = ref(false);
const activeExerciseIndex = ref(null);
const exerciseOptions = ref([]);
const showDropdownIndex = ref(null);
const searchQuery = ref('');
const draggedIndex = ref(null);
const dragOverIndex = ref(null);

watch(
  () => props.modelValue,
  (newValue) => {
    exercises.value = newValue;
  },
);

function getMaxSets(exSet) {
  if (!exSet || !Array.isArray(exSet)) {
    return 0;
  }
  return exSet.reduce((max, ex) => {
    if (ex.sets && Array.isArray(ex.sets)) {
      return Math.max(max, ex.sets.length);
    }
    return max;
  }, 0);
}

function filterOptions(index) {
  showDropdownIndex.value = index;
}

function validateEx() {
  let isValid = true;
  const updated = exercises.value.map((entry) => {
    const newEntry = {
      ...entry,
      isValid: true,
      errors: {
        name: false,
        reps: [],
        weight: [],
      },
    };
    if (!newEntry.name?.trim() || newEntry.name.length < 3) {
      newEntry.errors.name = true;
      isValid = false;
    }
    newEntry.sets = newEntry.sets.map((set, index) => {
      // Validate reps
      if (typeof set.reps === 'number' && set.reps > 0 && set.reps <= 99) {
        newEntry.errors.reps[index] = false;
      } else {
        newEntry.errors.reps[index] = true;
        isValid = false;
      }

      // Validate weight
      if (typeof set.weight === 'number' && set.weight >= 0 && set.weight <= 999) {
        newEntry.errors.weight[index] = false;
      } else {
        newEntry.errors.weight[index] = true;
        isValid = false;
      }

      return {
        ...set,
      };
    });

    return newEntry;
  });
  updated.isValid = !isValid;
  exercises.value = updated;
  return isValid;
}

function updateValue() {
  const valid = validateEx();
  emit('update:modelValue', exercises.value);
  emit('update:isValid', valid);
}
function closeExerciseSelector() {
  offcanvasVisible.value = false;
  activeExerciseIndex.value = null;
  searchQuery.value = '';
}
function selectExerciseName(index, name) {
  if (index !== null && exercises.value[index]) {
    exercises.value[index].name = name;
    updateValue();
  }
  closeExerciseSelector();
}

function filteredOptions(input) {
  const term = input?.toLowerCase() || '';
  return exerciseOptions.value
    .filter((opt) => opt.toLowerCase().includes(term) && opt.toLowerCase() !== term)
    .slice(0, 10);
}

function syncData() {
  emit('update:modelValue', exercises.value);
}

function addSet(exerciseIndex) {
  const exercise = exercises.value[exerciseIndex];
  if (!exercise) {
    globalThis.$toast?.showToast(`Exercise at index ${exerciseIndex} not found.`, 'danger');
    return;
  }
  if (!exercise.sets) {
    exercise.sets = [];
  }
  if (exercise.sets.length >= 10) {
    globalThis.$toast?.showToast(t('errorsMsg.setsLimit'), 'dark');
    return;
  }
  exercise.sets.push({
    reps: exercise.sets[exercise.sets.length - 1]?.reps || 0,
    weight: exercise.sets[exercise.sets.length - 1]?.weight || 0,
  });
  syncData();
}

function addExercise() {
  if (exercises.value.length >= 10) {
    globalThis.$toast?.showToast(t('errorsMsg.exercisesLimit'), 'info');
    return;
  }
  exercises.value.push({
    name: '',
    sets: [{}],
  });
  syncData();
}

function removeExercise(index) {
  exercises.value.splice(index, 1);
  syncData();
}

function removeSet(exerciseIndex) {
  const exercise = exercises.value[exerciseIndex];
  if (!exercise) {
    globalThis.$toast?.showToast(`Exercise at index ${exerciseIndex} not found.`, 'danger');
    return;
  }
  if (exercise.sets.length <= 1) {
    return;
  }
  exercise.sets.splice(exercise.sets.length - 1, 1);
  syncData();
}

function openExerciseSelector(index, currentName) {
  activeExerciseIndex.value = index;
  searchQuery.value = currentName || '';
  offcanvasVisible.value = true;
}

// Drag & Drop
/* eslint no-param-reassign: "error" */
function onDragStart(event, index) {
  draggedIndex.value = index;
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', 'exercise');
}

function onDragOver(event, index) {
  event.preventDefault();
  dragOverIndex.value = index;
}

function onDragLeave() {
  dragOverIndex.value = null;
}

function onDrop(event, targetIndex) {
  event.preventDefault();
  const sourceIndex = draggedIndex.value;
  if (sourceIndex === null || sourceIndex === targetIndex) {
    dragOverIndex.value = null;
    return;
  }

  const moved = exercises.value.splice(sourceIndex, 1)[0];
  exercises.value.splice(targetIndex, 0, moved);
  syncData();
  draggedIndex.value = null;
  dragOverIndex.value = null;
}

onBeforeMount(async () => {
  if (props.edit) {
    try {
      const res = await getUniqueExs();
      exerciseOptions.value = res.data.data || [];
    } catch (err) {
      console.error('Failed to load exercise options:', err);
    }
  }
});
</script>

<style scoped>
.drag-over {
  outline: 2px dashed var(--bs-dark);
  outline-offset: -2px;
  position: relative;
  z-index: 1;
}
.drag-over::after {
  content: "Drop to apply";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: var(--bs-dark);
  color: var(--bs-white);
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  pointer-events: none;
  opacity: 0.9;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
}
</style>
